/**
 * Optimizer Control Center — row status, filtering and stats (pure)
 * For Media Devoted | Created By Ciro Autiero
 */

import type {
  OptimizerFilters,
  OptimizerRow,
  OptimizerRowStatus,
  OptimizerStats,
} from '@/features/optimizer-center/optimizer-center.types';
import {
  countryKeyToIso2,
  inferCountryNameFromText,
} from '@/utils/country-flag-emoji';

/**
 * Status chip precedence: STANDARD rows are inert (optimizer not driving),
 * the kill switch beats everything else, then a failed last run surfaces
 * as 'error' regardless of auto/manual.
 */
export function rowStatus(row: OptimizerRow): OptimizerRowStatus {
  if (row.mode === 'STANDARD') return 'standard';
  if (row.killSwitch) return 'paused';
  if (row.lastRunOk === false) return 'error';
  return row.enabled ? 'auto' : 'manual';
}

function matchesSearch(row: OptimizerRow, search: string): boolean {
  const q = search.trim().toLowerCase();
  if (!q) return true;
  return (
    row.campaignName.toLowerCase().includes(q) ||
    row.flowName.toLowerCase().includes(q) ||
    row.campaignId.toLowerCase().includes(q) ||
    row.flowId.toLowerCase().includes(q)
  );
}

/**
 * Effective country for a row: the structured country when present, else a
 * country inferred from the campaign name (e.g. "… - Australia - …"). Mirrors
 * the Scheduler country cell so the filter and the displayed flag agree.
 */
export function rowCountry(row: OptimizerRow): string {
  return row.country || inferCountryNameFromText(row.campaignName) || '';
}

function matchesCountry(row: OptimizerRow, country: string): boolean {
  if (!country || country === 'all') return true;
  return countryKeyToIso2(rowCountry(row)) === country;
}

function matchesTag(row: OptimizerRow, tag: string): boolean {
  if (!tag || tag === 'all') return true;
  if (tag === 'none') return !row.staticTag;
  return row.staticTag?.id === tag;
}

export function filterRows(
  rows: OptimizerRow[],
  filters: OptimizerFilters,
): OptimizerRow[] {
  return rows.filter((row) => {
    const matchesProvider =
      filters.provider === 'all' || row.provider === filters.provider;
    const matchesMode = filters.mode === 'all' || row.mode === filters.mode;
    const matchesStatus =
      filters.status === 'all' || rowStatus(row) === filters.status;
    return (
      matchesProvider &&
      matchesMode &&
      matchesStatus &&
      matchesCountry(row, filters.country) &&
      matchesTag(row, filters.tag) &&
      matchesSearch(row, filters.search)
    );
  });
}

/**
 * Distinct countries present across the rows, deduped by ISO2 with a
 * representative display name (prefers a full name over a bare 2-letter code).
 * Sorted by name — powers the Scheduler country filter options.
 */
export function distinctRowCountries(
  rows: OptimizerRow[],
): Array<{ iso2: string; name: string }> {
  const byIso = new Map<string, string>();
  for (const row of rows) {
    const raw = rowCountry(row);
    if (!raw) continue;
    const iso2 = countryKeyToIso2(raw);
    if (!iso2) continue;
    const existing = byIso.get(iso2);
    if (!existing || (existing.length <= 2 && raw.length > 2)) {
      byIso.set(iso2, raw);
    }
  }
  return [...byIso.entries()]
    .map(([iso2, name]) => ({ iso2, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function computeStats(rows: OptimizerRow[]): OptimizerStats {
  const statuses = rows.map(rowStatus);
  return {
    total: rows.length,
    auto: statuses.filter((s) => s === 'auto').length,
    paused: statuses.filter((s) => s === 'paused').length,
    errors: statuses.filter((s) => s === 'error').length,
  };
}
